import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  flightConfirmationCodeConfig,
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import type { CreateFlightItemViewModel } from '../Create/models'
import type { PatchFlightItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import AirlineAutocomplete from 'tripPlanner/components/AutocompleteInputs/AirlineAutocomplete'
import AirportAutocomplete from 'tripPlanner/components/AutocompleteInputs/AirportAutocomplete'

const DividerContainer = styled.div`
  margin-top: ${rem(40)};
  height: ${rem(24)};
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};
`

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

interface Props {
  tripId: string
}

function FlightFields({ tripId }: Props) {
  const { airportConfig, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateFlightItemViewModel | PatchFlightItemViewModel
  >()
  const { control, setValue, watch, register, getValues } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const watchBooked = watch('isBooked')

  const handleAirportOptionChange = useCallback(
    (place: 'origin' | 'destination', airport: App.AirportLocation) => {
      const placeType = place === 'origin' ? 'startPlace' : 'endPlace'

      if (place === 'origin') {
        setValue('originAirportCode', airport.airportCode)
      } else {
        setValue('destinationAirportCode', airport.airportCode)
      }

      if (airport) {
        if (airport.lt && airport.lg) {
          setValue(`${placeType}.type`, 'GEO')
          setValue(`${placeType}.point.lat`, airport.lt)
          setValue(`${placeType}.point.lng`, airport.lg)
        } else {
          setValue(`${placeType}.type`, 'CUSTOM')
        }

        setValue(`${placeType}.name`, airport.airportName)
        const airportLocation = intl.formatMessage(
          {
            id: 'QXEQyf5M9X5LJ9Kt615g',
            defaultMessage: '{cityAirportName}, {country}',
            description: 'Trip planner flight field airport city name and country separated by comma',
          },
          { cityAirportName: airport.cityAirportName, country: airport.country },
        )
        setValue(`${placeType}.address`, airportLocation)
        setValue(`${placeType}.region`, airportLocation)
      }
    },
    [setValue, intl],
  )

  const handleDepartureChange = useCallback(
    (selectedOption: App.AirportLocation) => {
      handleAirportOptionChange('origin', selectedOption)
    },
    [handleAirportOptionChange],
  )

  const handleArrivalChange = useCallback(
    (selectedOption: App.AirportLocation) => {
      handleAirportOptionChange('destination', selectedOption)
    },
    [handleAirportOptionChange],
  )

  const airlineLabel = intl.formatMessage({
    id: 'wlh2Cjn_JnA9R3wziwFJ',
    defaultMessage: 'Airline',
    description: 'Form label for airline name field in flight trip item editor',
  })

  const airlinePlaceholder = intl.formatMessage({
    id: 'EOcb59NRzfEPjlwbD0z5',
    defaultMessage: 'Search by airline name or code',
    description: 'Placeholder text for airline search input in flight trip item editor',
  })

  const flightNumberLabel = intl.formatMessage({
    id: 'x6Q2yd-eu9KSHF8I7G29',
    defaultMessage: 'Flight number',
    description: 'Form label for flight number field in flight trip item editor',
  })

  const departureDetailsHeading = intl.formatMessage({
    id: 'T44wd6Cs7qjJ-DzO-EQ1',
    defaultMessage: 'Departure details',
    description: 'Section heading for departure details in flight trip item editor',
  })

  const airportLabel = intl.formatMessage({
    id: '32oDsLUjse5EQLYEulPj',
    defaultMessage: 'Airport',
    description: 'Form label for airport field in flight trip item editor',
  })

  const airportPlaceholder = intl.formatMessage({
    id: '27gpqeDJmpxEMug0s7aZ',
    defaultMessage: 'Search by airport name or code',
    description: 'Placeholder text for airport search input in flight trip item editor',
  })

  const dateLabel = intl.formatMessage({
    id: '7iHTwF3yzuZv6nw5pQb1',
    defaultMessage: 'Date',
    description: 'Form label for date field in flight trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: 'dYlpVXE1eU2vc_nqvkZ_',
    defaultMessage: 'Time',
    description: 'Form label for time field in flight trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'o8l9g3dkr2VFWOjzhBAB',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in flight trip item editor',
  })

  const arrivalDetailsHeading = intl.formatMessage({
    id: 'DiuaIunk6l0h98gJ3HHe',
    defaultMessage: 'Arrival details',
    description: 'Section heading for arrival details in flight trip item editor',
  })

  const bookingReferenceLabel = intl.formatMessage({
    id: 'JGBAxEgbrV9Ogz13eqPQ',
    defaultMessage: 'Booking reference',
    description: 'Form label for booking reference field in flight trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'qSaECgYUWugulflnPiFN',
    defaultMessage: 'Price',
    description: 'Form label for price field in flight trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'sCk5RISH3IVjSUKIww2Y',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in flight trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: 'zlWhdClMMSCmKWjKfyfm',
    defaultMessage: 'Note',
    description: 'Form label for notes field in flight trip item editor',
  })

  const pricePlaceholder = intl.formatMessage({
    id: '11royVeeZsJKv8-4LbuQ',
    defaultMessage: '0.00',
    description: 'Placeholder for price input field in the pricing or cost breakdown section',
  })

  const websitePlaceholder = intl.formatMessage({
    id: 'YJDWIzhAMQIYT78QXghI',
    defaultMessage: 'https://',
    description: 'Input placeholder text for website URL input field',
  })

  const notePlaceholder = intl.formatMessage({
    id: '8lL-HnK60ahgBfwDB2Vn',
    defaultMessage: 'Add note here',
    description: 'Input placeholder text for notes textarea field in the trip planner item editor modal',
  })

  return (
    <StyledVerticalSpacer gap={16}>
      <VerticalSpacer gap={32}>
        <AirlineAutocomplete
          {...register('airlineName')}
          label={airlineLabel}
          placeholder={airlinePlaceholder}
          initAirlineName={getValues('airlineName') ?? undefined}
        />

        <Input
          {...register('flightNumber')}
          label={flightNumberLabel}
          placeholder={flightNumberLabel}
        />

        <Heading variant="heading4">{departureDetailsHeading}</Heading>
      </VerticalSpacer>

      <VerticalSpacer gap={32}>
        <AirportAutocomplete
          {...register('originAirportCode')}
          label={airportLabel}
          placeholder={airportPlaceholder}
          required
          onAirportSelect={handleDepartureChange}
          noParentAirports
        />

        <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
          <RHFDatePickerInput
            name="startDate"
            label={dateLabel}
            defaultMonth={defaultMonth}
            defaultYear={defaultYear}
          />
          <TimeInput
            {...register('startTime')}
            label={timeLabel}
            placeholder={selectTimePlaceholder}
          />
        </Group>
      </VerticalSpacer>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{arrivalDetailsHeading}</Heading>

      <VerticalSpacer gap={32}>
        <AirportAutocomplete
          {...register('destinationAirportCode', { ...airportConfig })}
          label={airportLabel}
          placeholder={airportPlaceholder}
          required
          onAirportSelect={handleArrivalChange}
          noParentAirports
        />

        <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
          <RHFDatePickerInput
            name="endDate"
            label={dateLabel}
            defaultMonth={defaultMonth}
            defaultYear={defaultYear}
          />
          <TimeInput
            {...register('endTime')}
            label={timeLabel}
            placeholder={selectTimePlaceholder}
          />
        </Group>
      </VerticalSpacer>

      {/* <CheckboxField
        defaultChecked={getValues('isEndDateSame')}
        label="Arrival day is the same as departure"
        onClick={(e) => setValue('isEndDateSame', e.currentTarget.checked)}
      /> */}

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <Group
          direction="horizontal"
          horizontalAlign="stretch"
          verticalAlign="center"
          gap={16}
        >
          {/* <Controller
          name="fareClass"
          control={control}
          render={({ field }) => {
            return (
              <Select {...field} label="Cabin class">
                {fareCabins.map((opt) => (
                  <option key={opt.value} value={opt.value} label={opt.label}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )
          }}
        /> */}
          {watchBooked && (
            <Input
              {...register('confirmationCode', {
                ...flightConfirmationCodeConfig,
              })}
              label={bookingReferenceLabel}
              placeholder={bookingReferenceLabel}
            />
          )}
        </Group>
        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          placeholder={notePlaceholder}
          label={noteLabel}
          size="tall"
          resize="vertical"
        />
      </ShowMore>
    </StyledVerticalSpacer>
  )
}

export default FlightFields
